/**
 * The practice chrome (AIC-463): while a practice session is active, a banner that stays under the mode band with the
 * company and Exit / Reset; when practice ended on its own (a reset, an ended session), a notice in a polite live
 * region. Reset asks in the page (ConfirmDialog, never window.confirm). On entering, the banner takes focus once.
 */
import { useEffect, useRef, useState } from "react";
import { describeError, useAction, usePortal } from "../api/hooks.ts";
import { ConfirmDialog } from "../components/ConfirmDialog.tsx";
import { Link } from "../router.tsx";
import { dismissPracticeNotice, endPractice, takePracticeEntered, usePracticeState } from "./practiceStore.ts";
import "./practice.css";

export const PRACTICE_BANNER_TEXT = "Practice — nothing here reaches vendors, Outlook or the IDC Data Layer";

function Banner() {
  const { practice } = usePortal();
  const { busy, error, run, clearError } = useAction();
  const [confirm, setConfirm] = useState(false);
  const region = useRef<HTMLElement>(null);
  const resetBtn = useRef<HTMLButtonElement>(null);
  const sessionId = practice?.id;
  useEffect(() => {
    if (!sessionId || !takePracticeEntered()) return;
    // After the shell has moved focus to the new page's <main> for the same navigation.
    const t = setTimeout(() => region.current?.focus({ preventScroll: true }), 0);
    return () => clearTimeout(t);
  }, [sessionId]);
  if (!practice) return null;
  const reset = async () => {
    setConfirm(false);
    const ok = await run((api) => api.resetPractice(practice.id).then(() => true));
    if (ok) endPractice({ notice: `Practice reset for ${practice.company}. Start a new one from the assignment whenever you like.` });
    else setTimeout(() => resetBtn.current?.focus(), 0);
  };
  return (
    <section ref={region} className="pr-banner" role="region" aria-label="Practice mode" tabIndex={-1}>
      <div className="pr-banner-text">
        <strong>{PRACTICE_BANNER_TEXT}</strong>
        <span className="pr-banner-co">Practising on <b>{practice.company}</b></span>
      </div>
      <div className="pr-banner-acts">
        <Link to="/practice/outbox" className="ipd-link">Practice outbox</Link>
        <button type="button" className="ipd-btn" onClick={() => endPractice()}>Exit practice</button>
        <button type="button" ref={resetBtn} className="ipd-btn" aria-disabled={busy || undefined} aria-busy={busy || undefined} onClick={() => { clearError(); if (!busy) setConfirm(true); }}>Reset practice</button>
      </div>
      {error ? <p className="pr-banner-err" role="alert">The practice session wasn't reset. {describeError(error)}</p> : null}
      <ConfirmDialog
        open={confirm}
        title="Reset this practice session?"
        body={<>The practice copy of {practice.company}, its links and its outbox are set aside, and you return to the real assignment. Nothing real is changed. Start a new practice any time.</>}
        confirmLabel="Reset practice"
        cancelLabel="Keep practising"
        onConfirm={() => void reset()}
        onCancel={() => setConfirm(false)}
      />
    </section>
  );
}

export function PracticeChrome() {
  const state = usePracticeState();
  return (
    <>
      <div className="pr-live" aria-live="polite" aria-atomic="true">
        {state.notice && !state.session ? (
          <div className="ipd-notice pr-notice">
            <span>{state.notice}</span>
            <button type="button" className="ipd-link" onClick={dismissPracticeNotice}>Dismiss</button>
          </div>
        ) : null}
      </div>
      {state.session ? <Banner /> : null}
    </>
  );
}
